import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrow-down.svg";
import { ReactComponent as DotsIcon } from "../../assets/icons/dots.svg";
import Button from "../../components/Button";
import { Checkbox } from "../../components/Checkbox";
import MonthPicker from "../../components/MonthPicker";
import { Page } from "../../components/Page";
import UIPopover from "../../components/Popover";
import { ShowToast } from "../../components/toast";
import { api } from "../../helpers/api";
import { ReportingTab as ReportingTabComponent } from "./components/ReportingTab";

// =============================================================================
// TYPES
// =============================================================================

type CampaignStatus =
  | "draft"
  | "pending_approval"
  | "active"
  | "paused"
  | "error";
type CampaignType = "brand" | "competitor" | "remarketing";

type Campaign = {
  id: string;
  name: string;
  type: CampaignType;
  status: CampaignStatus;
  dailyBudget: number;
  biddingStrategy: string;
  googleAdsCampaignId?: string;
  deployedAt?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    googleAdsCustomerId?: string;
  };
};

type RecommendationType =
  | "bid_increase"
  | "bid_decrease"
  | "keyword_pause"
  | "keyword_add"
  | "keyword_remove"
  | "budget_increase"
  | "budget_decrease"
  | "ad_copy_update"
  | "targeting_adjustment";

type RecommendationStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "applied"
  | "failed";
type RecommendationPriority = "low" | "medium" | "high" | "critical";

interface Recommendation {
  id: string;
  type: RecommendationType;
  status: RecommendationStatus;
  priority: RecommendationPriority;
  description: string;
  reasoning: string;
  currentValue?: any;
  suggestedValue?: any;
  expectedImpact?: string;
  confidenceScore?: number;
  adminNotes?: string;
  appliedAt?: string;
  createdAt: string;
  campaign?: {
    id: string;
    name: string;
    type: string;
    userId: string;
    user?: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
  };
}

type UserWithCampaigns = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  hotelName?: string;
  googleAdsCustomerId?: string;
  googleAnalyticsPropertyId?: string;
  campaignCount: number;
};

// =============================================================================
// CONSTANTS
// =============================================================================

const statusColors: Record<CampaignStatus, string> = {
  draft: "bg-surface-secondary text-content-primary border-border-primary",
  pending_approval: "bg-yellow-100 text-yellow-800 border-yellow-300",
  active: "bg-green-100 text-green-800 border-green-300",
  paused: "bg-orange-100 text-orange-800 border-orange-300",
  error: "bg-red-100 text-red-800 border-red-300",
};

const typeLabels: Record<CampaignType, string> = {
  brand: "Brand Protection",
  competitor: "Competitor Conquest",
  remarketing: "Remarketing",
};

const statusLabels: Record<CampaignStatus, string> = {
  draft: "Draft",
  pending_approval: "Pending Approval",
  active: "Active",
  paused: "Paused",
  error: "Error",
};

const priorityColors: Record<RecommendationPriority, string> = {
  low: "bg-surface-secondary text-content-primary border-border-primary",
  medium: "bg-blue-100 text-blue-800 border-blue-300",
  high: "bg-orange-100 text-orange-800 border-orange-300",
  critical: "bg-red-100 text-red-800 border-red-300",
};

const recommendationTypeLabels: Record<RecommendationType, string> = {
  bid_increase: "Increase Bid",
  bid_decrease: "Decrease Bid",
  keyword_pause: "Pause Keyword",
  keyword_add: "Add Keyword",
  keyword_remove: "Remove Keyword",
  budget_increase: "Increase Budget",
  budget_decrease: "Decrease Budget",
  ad_copy_update: "Update Ad Copy",
  targeting_adjustment: "Adjust Targeting",
};

const typeIcons: Record<RecommendationType, string> = {
  bid_increase: "↑",
  bid_decrease: "↓",
  keyword_pause: "⏸",
  keyword_add: "+",
  keyword_remove: "−",
  budget_increase: "$↑",
  budget_decrease: "$↓",
  ad_copy_update: "✎",
  targeting_adjustment: "⎯",
};

const TABS = [
  { id: "settings", label: "Settings" },
  { id: "recommendations", label: "Recommendations" },
  { id: "reporting", label: "Reporting" },
] as const;

type TabId = (typeof TABS)[number]["id"];

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function Marketing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as TabId) || "settings";
  const selectedUserId = searchParams.get("userId") || "";

  const setActiveTab = (tab: TabId) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", tab);
    setSearchParams(params);
  };

  const setSelectedUserId = (userId: string) => {
    const params = new URLSearchParams(searchParams);
    if (userId) {
      params.set("userId", userId);
    } else {
      params.delete("userId");
    }
    setSearchParams(params);
  };

  // Fetch all users for the dropdown
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ["all-users"],
    queryFn: () => api.getUsers({ page: 0, limit: 100 }),
  });

  // Fetch full user details when a user is selected (includes Google IDs)
  const { data: selectedUserDetails, isLoading: isLoadingUserDetails } =
    useQuery({
      queryKey: ["user-details", selectedUserId],
      queryFn: () => api.getUser(selectedUserId),
      enabled: !!selectedUserId,
    });

  // Map users to include hotel name from user object
  const users: UserWithCampaigns[] = (usersData || []).map((user: any) => ({
    id: user.id,
    email: user.email,
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    hotelName:
      user.userMetadata?.hotelName || user.firstName || "Unknown Hotel",
    googleAdsCustomerId: "", // Will be fetched when selected
    googleAnalyticsPropertyId: "", // Will be fetched when selected
    campaignCount: 0,
  }));

  // Use the full user details for the selected user (includes Google IDs)
  const selectedUser: UserWithCampaigns | undefined = selectedUserDetails
    ? {
        id: selectedUserDetails.id,
        email: selectedUserDetails.email,
        firstName: selectedUserDetails.firstName || "",
        lastName: selectedUserDetails.lastName || "",
        hotelName:
          selectedUserDetails.userMetadata?.hotelName ||
          selectedUserDetails.firstName ||
          "Unknown Hotel",
        googleAdsCustomerId: selectedUserDetails.googleAdsCustomerId || "",
        googleAnalyticsPropertyId:
          selectedUserDetails.googleAnalyticsPropertyId || "",
        campaignCount: 0,
      }
    : users.find((u) => u.id === selectedUserId);

  return (
    <Page>
      <div className="p-4 md:h-[calc(100dvh-62px)] overflow-auto lg:h-[100dvh] bg-surface-tertiary flex-1 1xl:w-[unset]">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
          <h1 className="text-2xl font-semibold text-content-primary">
            Marketing
          </h1>
        </div>

        {/* User Selector */}
        <div className="mb-4 bg-surface-primary rounded-lg p-4 shadow-sm border border-border-secondary">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <label className="text-sm font-medium text-content-primary whitespace-nowrap">
              Select Hotel:
            </label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="flex-1 max-w-md px-3 py-2 border border-border-primary rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface-primary"
              disabled={isLoadingUsers}
            >
              <option value="">
                {isLoadingUsers ? "Loading..." : "-- Select a hotel --"}
              </option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.hotelName || `${user.firstName} ${user.lastName}`} (
                  {user.email})
                </option>
              ))}
            </select>
            {selectedUserId && (
              <div className="text-xs text-content-tertiary">
                {isLoadingUserDetails ? (
                  "Loading..."
                ) : selectedUser?.googleAdsCustomerId ? (
                  <span className="text-green-600">
                    Google Ads ID: {selectedUser.googleAdsCustomerId}
                  </span>
                ) : (
                  <span className="text-orange-500">
                    No Google Ads ID configured
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-surface-primary rounded-t-lg border-b border-border-secondary shadow-sm">
          <div className="flex">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  "px-6 py-3 text-sm font-medium transition-colors border-b-2",
                  activeTab === tab.id
                    ? "text-primary border-primary bg-primary/5"
                    : "text-content-tertiary border-transparent hover:text-content-primary hover:bg-surface-secondary",
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-surface-primary rounded-b-lg shadow-sm border border-t-0 border-border-secondary">
          {!selectedUserId ? (
            <div className="flex flex-col items-center justify-center py-16 text-content-tertiary">
              <div className="text-5xl mb-4">🏨</div>
              <p className="text-lg font-medium">
                Select a hotel to get started
              </p>
              <p className="text-sm mt-1">
                Choose a hotel from the dropdown above to view their marketing
                data
              </p>
            </div>
          ) : (
            <>
              {activeTab === "settings" && (
                <SettingsTab
                  userId={selectedUserId}
                  selectedUser={selectedUser}
                />
              )}
              {activeTab === "recommendations" && (
                <RecommendationsTab userId={selectedUserId} />
              )}
              {activeTab === "reporting" && (
                <ReportingTab userId={selectedUserId} />
              )}
            </>
          )}
        </div>
      </div>
    </Page>
  );
}

// =============================================================================
// SETTINGS TAB (Campaign List & Management)
// =============================================================================

function SettingsTab({
  userId,
  selectedUser,
}: {
  userId: string;
  selectedUser?: UserWithCampaigns;
}) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [tableData, setTableData] = useState<Campaign[]>([]);
  const [statusFilter, setStatusFilter] = useState<
    CampaignStatus | undefined
  >();
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [showGoogleConfig, setShowGoogleConfig] = useState(false);
  const [googleAdsId, setGoogleAdsId] = useState(
    selectedUser?.googleAdsCustomerId || "",
  );
  const [googleAnalyticsId, setGoogleAnalyticsId] = useState(
    selectedUser?.googleAnalyticsPropertyId || "",
  );
  const loadMoreRef = useRef<any>(null);
  const queryClient = useQueryClient();

  // Update local state when selectedUser changes
  useEffect(() => {
    setGoogleAdsId(selectedUser?.googleAdsCustomerId || "");
    setGoogleAnalyticsId(selectedUser?.googleAnalyticsPropertyId || "");
  }, [selectedUser]);

  const columnHelper = createColumnHelper<Campaign>();

  const fetchCampaigns = async ({ pageParam }: { pageParam: number }) => {
    const res = await api.campaigns.list({
      userId,
      page: pageParam,
      limit: 20,
      status: statusFilter,
    });
    return res.campaigns || [];
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["campaigns", userId, statusFilter],
    queryFn: fetchCampaigns,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages, pageParam) => {
      if (lastPage.length < 20) return null;
      return pageParam + 1;
    },
    enabled: !!userId,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: CampaignStatus }) =>
      api.campaigns.updateStatus(id, status),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Campaign status updated" });
      refetch();
    },
    onError: () => {
      ShowToast({ type: "error", message: "Failed to update campaign status" });
    },
  });

  const columns = [
    columnHelper.accessor("name", {
      header: "Campaign Name",
      cell: (info) => (
        <div className="flex flex-col gap-1">
          <span className="font-medium">{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor("type", {
      header: "Type",
      cell: (info) => (
        <span className="text-sm">{typeLabels[info.getValue()]}</span>
      ),
    }),
    columnHelper.accessor("status", {
      header: "Status",
      cell: (info) => (
        <div
          className={`${statusColors[info.getValue()]} text-xs font-medium border rounded py-1 px-2 flex items-center justify-center w-fit`}
        >
          {statusLabels[info.getValue()]}
        </div>
      ),
    }),
    columnHelper.accessor("dailyBudget", {
      header: "Daily Budget",
      cell: (info) => (
        <span className="font-medium">
          ${Number(info.getValue()).toFixed(2)}
        </span>
      ),
    }),
    columnHelper.accessor("createdAt", {
      header: "Created",
      cell: (info) => (
        <div className="flex flex-row justify-between w-full items-center">
          {info.getValue()
            ? moment(info.getValue()).format("MMM DD, YYYY")
            : "-"}
          <UIPopover
            trigger={
              <button className="!rounded-md h-[2.5rem] w-[2.5rem] flex !p-2 items-center justify-center [&_circle]:fill-black [&_circle]:hover:fill-white">
                <DotsIcon />
              </button>
            }
          >
            {(close) => (
              <div className="flex flex-col text-xs text-content-primary font-medium gap-3 bg-surface-primary p-4 rounded-lg shadow-[0px_8px_28px_0px_rgba(20,20,43,0.10)] font-inter mr-16 min-w-[150px]">
                <button
                  className="text-start hover:text-primary"
                  onClick={() => {
                    setEditingCampaign(info.row.original);
                    close();
                  }}
                >
                  Edit Campaign
                </button>
                {info.row.original.status === "active" && (
                  <button
                    className="text-start hover:text-orange-600"
                    onClick={() => {
                      updateStatusMutation.mutate({
                        id: info.row.original.id,
                        status: "paused",
                      });
                      close();
                    }}
                  >
                    Pause Campaign
                  </button>
                )}
                {info.row.original.status === "paused" && (
                  <button
                    className="text-start hover:text-green-600"
                    onClick={() => {
                      updateStatusMutation.mutate({
                        id: info.row.original.id,
                        status: "active",
                      });
                      close();
                    }}
                  >
                    Activate Campaign
                  </button>
                )}
                {info.row.original.status === "draft" && (
                  <button
                    className="text-start hover:text-primary"
                    onClick={() => {
                      updateStatusMutation.mutate({
                        id: info.row.original.id,
                        status: "pending_approval",
                      });
                      close();
                    }}
                  >
                    Submit for Approval
                  </button>
                )}
              </div>
            )}
          </UIPopover>
        </div>
      ),
    }),
  ];

  const table = useReactTable<Campaign>({
    data: tableData || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
  });

  useMemo(() => {
    if (data) {
      const newData = data.pages.flatMap((page) => page);
      setTableData(newData);
    }
  }, [data]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: null, rootMargin: "20px", threshold: 1.0 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const statusOptions: { value: CampaignStatus | undefined; label: string }[] =
    [
      { value: undefined, label: "All Statuses" },
      { value: "draft", label: "Draft" },
      { value: "pending_approval", label: "Pending Approval" },
      { value: "active", label: "Active" },
      { value: "paused", label: "Paused" },
      { value: "error", label: "Error" },
    ];

  // Mutation to update Google IDs
  const updateGoogleIdsMutation = useMutation({
    mutationFn: (data: {
      googleAdsCustomerId: string;
      googleAnalyticsPropertyId: string;
    }) => api.putUserProfile(userId, data),
    onSuccess: () => {
      ShowToast({
        type: "success",
        message: "Google integration settings updated",
      });
      queryClient.invalidateQueries({ queryKey: ["user-details", userId] });
      setShowGoogleConfig(false);
    },
    onError: () => {
      ShowToast({
        type: "error",
        message: "Failed to update Google integration settings",
      });
    },
  });

  const handleSaveGoogleConfig = () => {
    updateGoogleIdsMutation.mutate({
      googleAdsCustomerId: googleAdsId,
      googleAnalyticsPropertyId: googleAnalyticsId,
    });
  };

  return (
    <div className="p-4">
      {/* Campaign Edit Modal */}
      {editingCampaign && (
        <CampaignEditModal
          campaign={editingCampaign}
          onClose={() => setEditingCampaign(null)}
          onSave={() => {
            setEditingCampaign(null);
            refetch();
          }}
        />
      )}

      {/* Google Integration Configuration Section */}
      <div className="mb-6 bg-surface-secondary rounded-lg border border-border-secondary overflow-hidden">
        <button
          type="button"
          onClick={() => setShowGoogleConfig(!showGoogleConfig)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-surface-secondary transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-surface-primary rounded-lg shadow-sm flex items-center justify-center">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
            </div>
            <div className="text-left">
              <h3 className="text-sm font-semibold text-content-primary">
                Google Integration
              </h3>
              <p className="text-xs text-content-tertiary">
                {selectedUser?.googleAdsCustomerId ||
                selectedUser?.googleAnalyticsPropertyId
                  ? "Connected"
                  : "Configure Google Ads and Analytics IDs"}
              </p>
            </div>
          </div>
          <ArrowDownIcon
            className={`w-4 h-4 transition-transform ${showGoogleConfig ? "rotate-180" : ""}`}
          />
        </button>

        {showGoogleConfig && (
          <div className="px-4 pb-4 border-t border-border-secondary bg-surface-primary">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-content-primary mb-1">
                  Google Ads Customer ID
                </label>
                <input
                  type="text"
                  value={googleAdsId}
                  onChange={(e) => setGoogleAdsId(e.target.value)}
                  placeholder="e.g., 123-456-7890"
                  className="w-full px-3 py-2 border border-border-primary rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-content-tertiary mt-1">
                  Found in Google Ads under "Tools & Settings" → "Account
                  Access"
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-content-primary mb-1">
                  Google Analytics Property ID
                </label>
                <input
                  type="text"
                  value={googleAnalyticsId}
                  onChange={(e) => setGoogleAnalyticsId(e.target.value)}
                  placeholder="e.g., 123456789"
                  className="w-full px-3 py-2 border border-border-primary rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-content-tertiary mt-1">
                  Found in Google Analytics under "Admin" → "Property Settings"
                </p>
              </div>
            </div>
            <div className="flex justify-end mt-4 gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setGoogleAdsId(selectedUser?.googleAdsCustomerId || "");
                  setGoogleAnalyticsId(
                    selectedUser?.googleAnalyticsPropertyId || "",
                  );
                  setShowGoogleConfig(false);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveGoogleConfig}
                disabled={updateGoogleIdsMutation.isPending}
              >
                {updateGoogleIdsMutation.isPending
                  ? "Saving..."
                  : "Save Configuration"}
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-row mb-4 justify-between items-center gap-4">
        <h2 className="text-lg font-semibold">Campaign Settings</h2>
        <select
          value={statusFilter || ""}
          onChange={(e) =>
            setStatusFilter((e.target.value as CampaignStatus) || undefined)
          }
          className="px-3 py-2 border border-border-primary rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {statusOptions.map((opt) => (
            <option key={opt.label} value={opt.value || ""}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-auto max-h-[calc(100vh-380px)] relative flex flex-col">
        <table className="w-full relative">
          <colgroup>
            <col style={{ width: "30%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "20%" }} />
          </colgroup>
          <thead className="w-full">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="sticky top-0 z-10">
                {headerGroup.headers.map((header, index) => (
                  <th
                    key={header.id}
                    className="text-[0.875rem] font-medium text-[#202020] text-start p-0"
                  >
                    <div
                      className={clsx(
                        "w-full px-5 py-3 bg-[#F4F4F6] flex flex-row items-center gap-1 transition-all",
                        index === headerGroup.headers.length - 1 &&
                          "border-r rounded-tr-[8px] rounded-br-[8px]",
                        index === 0 &&
                          "border-l rounded-tl-[8px] rounded-bl-[8px]",
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {!isLoading &&
              table.getRowModel().rows.map((row, index) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={clsx(
                        "px-5 py-3 border-b border-b-[#D7D7D7] font-medium text-[0.8125rem]",
                        index === table.getRowModel().rows.length - 1 &&
                          "border-b-0",
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}

            {isFetching && <TableSkeleton rows={3} columns={5} />}
          </tbody>
        </table>

        {!isLoading && !isFetching && tableData.length === 0 && (
          <div className="flex-1 flex flex-col justify-center items-center py-12">
            <div className="text-5xl mb-4">📢</div>
            <span className="text-[#999999] text-[1rem] font-medium">
              No campaigns found
            </span>
            <span className="text-[#bbbbbb] text-[0.875rem] mt-1">
              This hotel doesn't have any campaigns yet
            </span>
          </div>
        )}

        <div>
          <button
            onClick={() => fetchNextPage()}
            disabled={!hasNextPage || isFetchingNextPage}
            ref={loadMoreRef}
          />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// CAMPAIGN EDIT MODAL
// =============================================================================

function CampaignEditModal({
  campaign,
  onClose,
  onSave,
}: {
  campaign: Campaign;
  onClose: () => void;
  onSave: () => void;
}) {
  const [name, setName] = useState(campaign.name);
  const [dailyBudget, setDailyBudget] = useState(
    campaign.dailyBudget.toString(),
  );
  const [biddingStrategy, setBiddingStrategy] = useState(
    campaign.biddingStrategy,
  );

  const updateMutation = useMutation({
    mutationFn: (data: {
      name: string;
      dailyBudget: number;
      biddingStrategy: string;
    }) => api.campaigns.update(campaign.id, data),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Campaign updated successfully" });
      onSave();
    },
    onError: () => {
      ShowToast({ type: "error", message: "Failed to update campaign" });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({
      name,
      dailyBudget: parseFloat(dailyBudget),
      biddingStrategy,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-surface-primary rounded-lg p-6 w-full max-w-md shadow-xl">
        <h3 className="text-lg font-semibold mb-4">Edit Campaign</h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-content-primary mb-1">
              Campaign Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-content-primary mb-1">
              Daily Budget ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={dailyBudget}
              onChange={(e) => setDailyBudget(e.target.value)}
              className="w-full px-3 py-2 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-content-primary mb-1">
              Bidding Strategy
            </label>
            <select
              value={biddingStrategy}
              onChange={(e) => setBiddingStrategy(e.target.value)}
              className="w-full px-3 py-2 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="MAXIMIZE_CLICKS">Maximize Clicks</option>
              <option value="MAXIMIZE_CONVERSIONS">Maximize Conversions</option>
              <option value="TARGET_CPA">Target CPA</option>
              <option value="TARGET_ROAS">Target ROAS</option>
            </select>
          </div>
          <div className="flex gap-3 mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateMutation.isPending}
              className="flex-1"
            >
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// RECOMMENDATIONS TAB
// =============================================================================

function RecommendationsTab({ userId }: { userId: string }) {
  const [tableData, setTableData] = useState<Recommendation[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const loadMoreRef = useRef<any>(null);

  const columnHelper = createColumnHelper<Recommendation>();

  const fetchRecommendations = async ({ pageParam }: { pageParam: number }) => {
    // Filter recommendations by userId through campaign relationship
    const res = await api.recommendations.listPending({
      page: pageParam,
      limit: 20,
    });
    // Filter client-side by userId
    const allRecs = res.recommendations || [];
    return allRecs.filter((r: Recommendation) => r.campaign?.userId === userId);
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["recommendations", userId],
    queryFn: fetchRecommendations,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages, pageParam) => {
      if (lastPage.length < 20) return null;
      return pageParam + 1;
    },
    enabled: !!userId,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({
      id,
      status,
      adminNotes,
    }: {
      id: string;
      status: "approved" | "rejected";
      adminNotes?: string;
    }) => api.recommendations.updateStatus(id, { status, adminNotes }),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Recommendation updated" });
      refetch();
      setSelectedIds(new Set());
    },
    onError: () => {
      ShowToast({ type: "error", message: "Failed to update recommendation" });
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: ({
      recommendationIds,
      status,
    }: {
      recommendationIds: string[];
      status: "approved" | "rejected";
    }) => api.recommendations.bulkUpdate({ recommendationIds, status }),
    onSuccess: (data) => {
      ShowToast({
        type: "success",
        message: `${data.updated} recommendations updated`,
      });
      refetch();
      setSelectedIds(new Set());
    },
    onError: () => {
      ShowToast({ type: "error", message: "Failed to update recommendations" });
    },
  });

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === tableData.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(tableData.map((r) => r.id)));
    }
  };

  const handleBulkAction = (status: "approved" | "rejected") => {
    if (selectedIds.size === 0) return;
    bulkUpdateMutation.mutate({
      recommendationIds: Array.from(selectedIds),
      status,
    });
  };

  const columns = [
    columnHelper.display({
      id: "select",
      header: () => (
        <Checkbox
          id="select-all"
          name="select-all"
          checked={
            selectedIds.size > 0 && selectedIds.size === tableData.length
          }
          onChange={toggleSelectAll}
        />
      ),
      cell: (info) => (
        <Checkbox
          id={`select-${info.row.original.id}`}
          name={`select-${info.row.original.id}`}
          checked={selectedIds.has(info.row.original.id)}
          onChange={() => toggleSelect(info.row.original.id)}
        />
      ),
    }),
    columnHelper.accessor("type", {
      header: "Type",
      cell: (info) => (
        <div className="flex items-center gap-2">
          <span className="text-lg">{typeIcons[info.getValue()]}</span>
          <span className="text-sm">
            {recommendationTypeLabels[info.getValue()]}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor("description", {
      header: "Recommendation",
      cell: (info) => (
        <div className="max-w-md">
          <p className="text-sm font-medium text-content-primary line-clamp-2">
            {info.getValue()}
          </p>
          {info.row.original.campaign && (
            <p className="text-xs text-content-tertiary mt-1">
              Campaign: {info.row.original.campaign.name}
            </p>
          )}
        </div>
      ),
    }),
    columnHelper.accessor("priority", {
      header: "Priority",
      cell: (info) => (
        <div
          className={`${priorityColors[info.getValue()]} text-xs font-medium border rounded py-1 px-2 flex items-center justify-center w-fit capitalize`}
        >
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor("confidenceScore", {
      header: "Confidence",
      cell: (info) => {
        const score = info.getValue();
        if (!score) return <span className="text-gray-400">-</span>;
        return (
          <div className="flex items-center gap-2">
            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${
                  score >= 0.8
                    ? "bg-green-500"
                    : score >= 0.6
                      ? "bg-yellow-500"
                      : "bg-red-500"
                }`}
                style={{ width: `${score * 100}%` }}
              />
            </div>
            <span className="text-xs text-content-secondary">
              {Math.round(score * 100)}%
            </span>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "Actions",
      cell: (info) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() =>
              updateStatusMutation.mutate({
                id: info.row.original.id,
                status: "approved",
              })
            }
            disabled={updateStatusMutation.isPending}
            className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
          >
            Approve
          </button>
          <button
            onClick={() =>
              updateStatusMutation.mutate({
                id: info.row.original.id,
                status: "rejected",
              })
            }
            disabled={updateStatusMutation.isPending}
            className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
          >
            Reject
          </button>
          <button
            onClick={() =>
              setExpandedId(
                expandedId === info.row.original.id
                  ? null
                  : info.row.original.id,
              )
            }
            className="px-2 py-1.5 text-xs font-medium text-content-secondary bg-surface-secondary rounded hover:bg-surface-hover"
          >
            {expandedId === info.row.original.id ? "Hide" : "Details"}
          </button>
        </div>
      ),
    }),
  ];

  const table = useReactTable<Recommendation>({
    data: tableData || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useMemo(() => {
    if (data) {
      const newData = data.pages.flatMap((page) => page);
      setTableData(newData);
    }
  }, [data]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: null, rootMargin: "20px", threshold: 1.0 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">
            Optimization Recommendations
          </h2>
          {tableData.length > 0 && (
            <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
              {tableData.length} pending
            </span>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
          <span className="text-sm text-blue-800">
            {selectedIds.size} recommendation{selectedIds.size > 1 ? "s" : ""}{" "}
            selected
          </span>
          <div className="flex gap-2">
            <Button
              onClick={() => handleBulkAction("approved")}
              disabled={bulkUpdateMutation.isPending}
            >
              Approve All
            </Button>
            <Button
              variant="outline"
              onClick={() => handleBulkAction("rejected")}
              disabled={bulkUpdateMutation.isPending}
            >
              Reject All
            </Button>
            <Button variant="link" onClick={() => setSelectedIds(new Set())}>
              Clear
            </Button>
          </div>
        </div>
      )}

      <div className="overflow-auto max-h-[calc(100vh-420px)] relative flex flex-col">
        <table className="w-full relative">
          <colgroup>
            <col style={{ width: "40px" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "35%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "20%" }} />
          </colgroup>
          <thead className="w-full">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="sticky top-0 z-10">
                {headerGroup.headers.map((header, index) => (
                  <th
                    key={header.id}
                    className="text-[0.875rem] font-medium text-[#202020] text-start p-0"
                  >
                    <div
                      className={clsx(
                        "w-full px-4 py-3 bg-[#F4F4F6] flex flex-row items-center gap-1 transition-all",
                        index === headerGroup.headers.length - 1 &&
                          "border-r rounded-tr-[8px] rounded-br-[8px]",
                        index === 0 &&
                          "border-l rounded-tl-[8px] rounded-bl-[8px]",
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {!isLoading &&
              table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <tr
                    className={clsx(
                      expandedId === row.original.id && "bg-surface-secondary",
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={clsx(
                          "px-4 py-3 border-b border-b-[#D7D7D7] font-medium text-[0.8125rem]",
                          expandedId === row.original.id && "border-b-0",
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                  {/* Expanded Details Row */}
                  {expandedId === row.original.id && (
                    <tr className="bg-surface-secondary">
                      <td
                        colSpan={6}
                        className="px-4 py-4 border-b border-b-[#D7D7D7]"
                      >
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <h4 className="font-medium text-content-primary mb-2">
                              Reasoning
                            </h4>
                            <p className="text-content-secondary">
                              {row.original.reasoning ||
                                "No reasoning provided"}
                            </p>
                          </div>
                          <div>
                            <h4 className="font-medium text-content-primary mb-2">
                              Expected Impact
                            </h4>
                            <p className="text-content-secondary">
                              {row.original.expectedImpact || "Not specified"}
                            </p>
                          </div>
                          {row.original.currentValue && (
                            <div>
                              <h4 className="font-medium text-content-primary mb-2">
                                Current Value
                              </h4>
                              <p className="text-content-secondary font-mono bg-surface-secondary p-2 rounded">
                                {JSON.stringify(
                                  row.original.currentValue,
                                  null,
                                  2,
                                )}
                              </p>
                            </div>
                          )}
                          {row.original.suggestedValue && (
                            <div>
                              <h4 className="font-medium text-content-primary mb-2">
                                Suggested Value
                              </h4>
                              <p className="text-content-secondary font-mono bg-green-50 p-2 rounded">
                                {JSON.stringify(
                                  row.original.suggestedValue,
                                  null,
                                  2,
                                )}
                              </p>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}

            {isFetching && <TableSkeleton rows={3} columns={6} />}
          </tbody>
        </table>

        {!isLoading && !isFetching && tableData.length === 0 && (
          <div className="flex-1 flex flex-col justify-center items-center py-12">
            <div className="text-6xl mb-4">🎉</div>
            <span className="text-[#999999] text-[1rem] font-medium">
              No pending recommendations
            </span>
            <span className="text-[#bbbbbb] text-[0.875rem] mt-1">
              All optimization recommendations have been reviewed
            </span>
          </div>
        )}

        <div>
          <button
            onClick={() => fetchNextPage()}
            disabled={!hasNextPage || isFetchingNextPage}
            ref={loadMoreRef}
          />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// REPORTING TAB
// =============================================================================

// Custom chart tooltip component
function ChartTooltip({ active, payload }: any) {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0]?.payload;

  return (
    <div className="bg-surface-primary rounded-lg shadow-lg border border-border-secondary p-3 min-w-[150px]">
      <div className="text-xs font-medium text-content-tertiary mb-2 text-center border-b pb-2">
        {data?.name}
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: "#9BCBD4" }}
            />
            <span className="text-xs">Impressions</span>
          </div>
          <span className="text-xs font-medium">
            {new Intl.NumberFormat("en-US").format(data?.impressions || 0)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: "#EC839D" }}
            />
            <span className="text-xs">Clicks</span>
          </div>
          <span className="text-xs font-medium">
            {new Intl.NumberFormat("en-US").format(data?.clicks || 0)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: "#EDD6AB" }}
            />
            <span className="text-xs">Conversions</span>
          </div>
          <span className="text-xs font-medium">
            {new Intl.NumberFormat("en-US").format(data?.purchases || 0)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: "#585858" }}
            />
            <span className="text-xs">Search Impr. Share</span>
          </div>
          <span className="text-xs font-medium">
            {(data?.searchImpressionShare || 0).toFixed(1)}%
          </span>
        </div>
      </div>
    </div>
  );
}

// Panel component for consistent styling
function Panel({
  label,
  children,
  className = "",
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`bg-surface-primary rounded-lg border border-border-secondary shadow-sm overflow-hidden ${className}`}
    >
      <div className="px-4 py-2.5 border-b border-border-secondary bg-surface-secondary">
        <h3 className="text-sm font-semibold text-content-primary">{label}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

// Reusable data table component
function DataTable({
  columns,
  data,
  emptyMessage = "No data available",
}: {
  columns: {
    key: string;
    label: string;
    align?: "left" | "right";
    render?: (value: any, row: any) => React.ReactNode;
  }[];
  data: any[];
  emptyMessage?: string;
}) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-content-tertiary text-sm">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overflow-auto max-h-[300px]">
      <table className="w-full">
        <thead className="sticky top-0 bg-surface-primary">
          <tr className="border-b border-dashed border-border-primary">
            {columns.map((col) => (
              <th
                key={col.key}
                className={`py-2 px-2 text-xs font-semibold text-content-primary whitespace-nowrap ${
                  col.align === "right" ? "text-right" : "text-left"
                }`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-dashed divide-gray-200">
          {data.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={
                rowIndex === data.length - 1
                  ? "border-t border-dashed border-gray-400 bg-surface-secondary"
                  : ""
              }
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`py-2.5 px-2 text-xs ${
                    col.align === "right" ? "text-right" : "text-left"
                  } ${rowIndex === data.length - 1 ? "font-semibold text-content-primary" : "text-content-tertiary"}`}
                >
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportingTab({ userId }: { userId: string }) {
  // Generate month options for dropdown (last 24 months + special options)
  const generateDateOptions = () => {
    const options: {
      value: string;
      label: string;
      startDate: string;
      endDate: string;
    }[] = [];

    // Add "Last 30 Days" option
    options.push({
      value: "L30",
      label: "Last 30 Days",
      startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
    });

    // Add "Year to Date" option
    options.push({
      value: "YTD",
      label: "Year to Date",
      startDate: moment().startOf("year").format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
    });

    // Add individual months (last 24 months)
    for (let i = 0; i < 24; i++) {
      const date = moment().subtract(i, "months");
      options.push({
        value: date.format("YYYY-MM"),
        label: date.format("MMMM YYYY"),
        startDate: date.startOf("month").format("YYYY-MM-DD"),
        endDate: date.endOf("month").format("YYYY-MM-DD"),
      });
    }

    return options;
  };

  const dateOptions = generateDateOptions();

  // Default to current month
  const [selectedValue, setSelectedValue] = useState(
    moment().format("YYYY-MM"),
  );
  const [filter, setFilter] = useState(() => {
    const currentMonth = dateOptions.find(
      (opt) => opt.value === moment().format("YYYY-MM"),
    );
    return {
      startDate:
        currentMonth?.startDate ||
        moment().startOf("month").format("YYYY-MM-DD"),
      endDate:
        currentMonth?.endDate || moment().endOf("month").format("YYYY-MM-DD"),
    };
  });

  // Handle dropdown change
  const handleDateChange = (value: string) => {
    const date = moment(value, "YYYY-MM");
    setSelectedValue(value);
    if (date.isValid()) {
      setFilter({
        startDate: date.clone().startOf("month").format("YYYY-MM-DD"),
        endDate: date.clone().endOf("month").format("YYYY-MM-DD"),
      });
    }
  };

  const { data: marketingData, isLoading } = useQuery({
    queryKey: ["marketing-analytics", userId, filter.startDate, filter.endDate],
    queryFn: () =>
      api.getMarketingAnalytics(userId, {
        startDate: filter.startDate,
        endDate: filter.endDate,
      }),
    enabled: !!userId,
  });

  // Format helper
  const formatNumber = (
    value: any,
    isPercentage = false,
    isCurrency = false,
    decimals = 0,
  ) => {
    if (value === undefined || value === null) return "-";
    const num = Number(value);
    if (isNaN(num)) return "-";

    if (isCurrency) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(num);
    }

    if (isPercentage) {
      return `${num.toFixed(decimals)}%`;
    }

    return new Intl.NumberFormat("en-US").format(num);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Transform data for display
  const trafficByDate = marketingData?.trafficByDate || [];
  const funnelSummary = marketingData?.funnelSummary || [];
  const campaignSummary = marketingData?.campaignSummary || [];
  const deviceSummary = marketingData?.deviceSummary || [];
  const abandonedCartSummary = marketingData?.abandonedCartSummary || [];
  const feederMarkets = marketingData?.feederMarkets || [];

  // Transform chart data
  const chartData = [...trafficByDate]
    .sort(
      (a: any, b: any) => moment(a.date).valueOf() - moment(b.date).valueOf(),
    )
    .map((item: any) => ({
      name: moment(item.date).format("MMM DD"),
      impressions: item.impressions || 0,
      clicks: item.clicks || 0,
      purchases: item.purchases || 0,
      searchImpressionShare: item.searchImpressionShare || 0,
    }));

  // Transform table data
  const funnelData = funnelSummary.map((item: any) => ({
    event: item.event,
    count: formatNumber(item.count),
    cvr: formatNumber(item.cvr, true, false, 0),
    value: formatNumber(item.value, false, true),
  }));

  const abandonedCartData = [
    { event: "-", count: "-", percentage: "-", value: "-" },
    ...abandonedCartSummary.map((item: any) => ({
      event: item.event?.replace(/([A-Z])/g, " $1").trim(),
      count: formatNumber(item.count),
      percentage: formatNumber(item.percent, true, false, 0),
      value: formatNumber(item.value, false, true),
    })),
  ];

  const campaignData = campaignSummary.map((item: any) => ({
    campaign: item.campaignName,
    impressions: formatNumber(item.impressions),
    clicks: formatNumber(item.clicks),
    ctr: formatNumber(item.ctr, true),
    atc: formatNumber(item.atc),
    bco: formatNumber(item.bco),
    purchases: formatNumber(item.pur),
    purchaseValue: formatNumber(item.purchaseValue, false, true),
    cvr: formatNumber(item.cvr, true, false, 0),
  }));

  const deviceData = deviceSummary.map((item: any) => ({
    device: item.device,
    clicks: formatNumber(item.clicks),
    ctr: formatNumber(item.ctr, true, false, 0),
    value: formatNumber(item.conversionValue, false, true),
  }));

  const feederData = feederMarkets.map((item: any) => ({
    market: item.city,
    impressions: formatNumber(item.impressions),
    clicks: formatNumber(item.clicks),
    value: formatNumber(item.conversions_value, false, true),
  }));

  const hasData =
    chartData.length > 0 || funnelData.length > 0 || campaignData.length > 0;

  return (
    <div>
      <div className="flex justify-between items-center px-4 pt-4">
        <h2 className="text-lg font-semibold text-content-primary">
          Marketing Performance
        </h2>
        <div className="flex items-center gap-3">
          <MonthPicker
            initialDate={selectedValue}
            onChange={(month, year) => {
              if (month && year) {
                handleDateChange(`${year}-${month}`);
              }
            }}
          />
        </div>
      </div>

      {!hasData ? (
        <div className="flex flex-col items-center justify-center py-16 text-content-tertiary">
          <div className="text-5xl mb-4">📊</div>
          <p className="text-lg font-medium">No marketing data available</p>
          <p className="text-sm mt-1">
            Marketing analytics will appear here once Google Ads data is synced
          </p>
        </div>
      ) : (
        <ReportingTabComponent userId={userId} data={marketingData} />
      )}
    </div>
  );
}

// =============================================================================
// SHARED COMPONENTS
// =============================================================================

function TableSkeleton({ rows, columns }: { rows: number; columns: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={`skeleton-row-${rowIndex}`} className="animate-pulse">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td
              key={`skeleton-cell-${rowIndex}-${colIndex}`}
              className="px-5 py-4 border-b border-b-[#D7D7D7] font-medium text-[0.8125rem]"
            >
              <div className="w-full h-4 bg-gray-300 rounded-md"></div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
